import { useState, useEffect, useMemo, PropsWithChildren } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import useCooperativeId from 'shared/hooks/useCooperativeId';
import useTranslation from 'shared/hooks/useTranslation';
import { useUpdateCaseNotifications } from 'shared/hooks/useUpdateCaseNotifications';
import type { Option } from 'shared/models/common';
import getSelectHelpers from 'shared/utils/getSelectHelpers';

import { keyOrdersApi } from '../../api';
import { Dropzone } from 'features/file-loader';

import type { FormState } from './KeyOrderCreateForm.type';
import { getDefaultOptionValue, transformAmountOfKeysInput } from './helpers';

import PageLayout from 'layouts/PageLayout';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Checkbox from 'shared/components/Checkbox';
import ControlLabel from 'shared/components/ControlLabel';
import Dialog from 'shared/components/Dialog';
import Input from 'shared/components/Input';
import LoadingButton from 'shared/components/LoadingButton';
import Select from 'shared/components/Select';
import SuccessfullyCreatedCaseDialog from 'shared/components/SuccessfullyCreatedCaseDialog';
import UnsavedChangesDialog from 'shared/components/UnsavedChangesDialog';
import ValidationWarning from 'components/validation-warning';
import { CheckboxLabel, StyledTextarea } from './KeyOrderCreateForm.styled';

const FieldContainer = ({ children }: PropsWithChildren<{}>) => (
  <Stack spacing={1}>{children}</Stack>
);
const CheckboxContainer = ({ children }: PropsWithChildren<{}>) => (
  <Stack direction="row" spacing={1.5}>
    {children}
  </Stack>
);

const ADDTIONAL_INFORMATION_MAX_LENGTH = 400;

const ValidationWarningMessage: Readonly<Record<string, string>> = {
  AmountOfKeysExceeded: 'Arvon on oltava suurempi kuin 0',
  get AdditionalInformationLengthExceedLimit() {
    return `Kentän maksimi merkkimäärä on ${ADDTIONAL_INFORMATION_MAX_LENGTH}`;
  },
  Required: 'Lisää puuttuva tieto',
};

type KeyOrderCreateFormProps = {
  onClose: () => void;
};

const KeyOrderCreateForm = ({
  onClose: onExternalClose,
}: KeyOrderCreateFormProps) => {
  const { t } = useTranslation();

  const coopId = useCooperativeId();
  const history = useHistory();

  const [
    getCreateFormOptions,
    { data: formOptions, isLoading, isFetching, isError },
  ] = keyOrdersApi.useLazyGetCreateFormOptionsQuery();
  const [
    createKeyOrderCase,
    { data: newKeyOrderCase, isLoading: isCreatingCase, isSuccess },
  ] = keyOrdersApi.useCreateKeyOrderCaseMutation();

  const {
    clearErrors,
    control,
    formState: { errors, isDirty },
    handleSubmit,
    register,
    reset,
  } = useForm<FormState>({
    criteriaMode: 'firstError',
    defaultValues: {
      additionalInformation: '',
      amountOfKeys: null,
      files: [],
      keyNumber: null,
      keyOrderType: null,
      sendEmail: false,
      sendSms: false,
      spaceId: null,
      typeOfTheKey: null,
    },
    reValidateMode: 'onSubmit',
  });

  const [showedFormDialog, showFormDialog] = useState(true);
  const [showedSuccessDialog, showSuccessDialog] = useState(false);
  const [showedUnsavedChangesDialog, showUnsavedChangesDialog] =
    useState(false);
  const updateCaseNotifications = useUpdateCaseNotifications();

  const submitForm = (data: FormState) => {
    if (!coopId || !data.amountOfKeys || !data.keyOrderType || !data.spaceId)
      return;

    createKeyOrderCase({
      body: {
        AdditionalInformation: data.additionalInformation,
        AmountOfKeys: data.amountOfKeys,
        HasDocuments: !!data.files.length,
        KeyNumber: data.keyNumber,
        KeyOrderType: data.keyOrderType,
        SendEmail: data.sendEmail,
        SendSms: data.sendSms,
        SpaceId: data.spaceId,
        TypeOfTheKey: data.typeOfTheKey,
      },
      cooperativeId: coopId,
      files: data.files,
    });
  };

  const handleCloseDialog = () => {
    if (isDirty) {
      showUnsavedChangesDialog(true);
    } else {
      showFormDialog(false);
    }
  };

  const handleCloseFormWithoutSaving = () => {
    showUnsavedChangesDialog(false);
    showFormDialog(false);
  };

  const handleCloseUnsavedChangesDialog = () => {
    showUnsavedChangesDialog(false);
  };

  const handleCloseSuccessDialog = () => {
    showSuccessDialog(false);
    showFormDialog(false);
  };

  const handleCloseSuccessDialogWithRedirect = () => {
    showSuccessDialog(false);
    showFormDialog(false);

    if (isSuccess && newKeyOrderCase) {
      history.push(`/key-order/${newKeyOrderCase.Id}`);
    }
  };

  const spaceSelectHelpers = useMemo(
    () => getSelectHelpers(formOptions?.Spaces ?? ([] as Option<string>[])),
    [formOptions?.Spaces]
  );

  const keyOrderTypesSelectHelpers = useMemo(
    () => getSelectHelpers(formOptions?.KeyOrderTypes ?? []),
    [formOptions?.KeyOrderTypes]
  );

  useEffect(() => {
    if (formOptions) {
      const defaultSpace = getDefaultOptionValue(formOptions.Spaces);
      const defaultKeyOrderType = getDefaultOptionValue(
        formOptions.KeyOrderTypes
      );

      reset({
        amountOfKeys: null,
        files: [],
        keyNumber: null,
        keyOrderType: defaultKeyOrderType?.Value ?? null,
        sendEmail: false,
        sendSms: false,
        spaceId: defaultSpace?.Value ?? null,
        typeOfTheKey: null,
      });
    }
  }, [formOptions]);

  useEffect(() => {
    if (coopId) {
      getCreateFormOptions(coopId);
    }
  }, [coopId]);

  useEffect(() => {
    if (isSuccess) {
      updateCaseNotifications();
      showSuccessDialog(true);
    }
  }, [isSuccess]);

  return (
    <Dialog
      open={showedFormDialog}
      fullWidth
      maxWidth="lg"
      onClose={handleCloseDialog}
      TransitionProps={{
        onExited: onExternalClose,
      }}
    >
      <UnsavedChangesDialog
        open={showedUnsavedChangesDialog}
        onApply={handleCloseFormWithoutSaving}
        onReject={handleCloseUnsavedChangesDialog}
      />
      {newKeyOrderCase && (
        <SuccessfullyCreatedCaseDialog
          description={`${t('#portal.incident.label.title')} #${
            newKeyOrderCase.Title
          }`}
          title={t('ServiceRequestSent') as string}
          open={showedSuccessDialog}
          onApply={handleCloseSuccessDialogWithRedirect}
          onClose={handleCloseSuccessDialog}
        />
      )}

      <PageLayout isLoading={isLoading || isFetching} isError={isError}>
        <Stack
          component="form"
          noValidate
          spacing={2.5}
          onSubmit={handleSubmit(submitForm)}
        >
          <DialogTitle>Avaintilaus</DialogTitle>
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            divider={<Divider orientation="vertical" flexItem />}
            spacing={2.5}
          >
            <Stack flex={1} spacing={2.5}>
              <FieldContainer>
                <ControlLabel required>
                  {t('##attribute#incident#uds_spaceid')}
                </ControlLabel>
                <ValidationWarning
                  text={errors.spaceId?.message}
                  handleInitErrors={() => clearErrors('spaceId')}
                  Component={
                    <Controller
                      control={control}
                      name="spaceId"
                      rules={{
                        required: ValidationWarningMessage.Required,
                      }}
                      render={({ field: { value, onChange } }) => (
                        <Select
                          value={value ?? ''}
                          variant="underline"
                          options={spaceSelectHelpers.options}
                          onChange={onChange}
                          getOptionLabel={spaceSelectHelpers.getOptionLabel}
                        />
                      )}
                    />
                  }
                />
              </FieldContainer>

              <FieldContainer>
                <ControlLabel required>
                  {t('##attribute#incident#uds_keyordertype')}
                </ControlLabel>
                <ValidationWarning
                  text={errors.keyOrderType?.message}
                  handleInitErrors={() => clearErrors('keyOrderType')}
                  Component={
                    <Controller
                      control={control}
                      name="keyOrderType"
                      rules={{
                        required: ValidationWarningMessage.Required,
                      }}
                      render={({ field: { value, onChange } }) => (
                        <Select
                          value={value ?? ''}
                          variant="underline"
                          options={keyOrderTypesSelectHelpers.options}
                          onChange={onChange}
                          getOptionLabel={
                            keyOrderTypesSelectHelpers.getOptionLabel
                          }
                        />
                      )}
                    />
                  }
                />
              </FieldContainer>

              <FieldContainer>
                <ControlLabel required>
                  {t('##attribute#incident#uds_keysamount')}
                </ControlLabel>
                <ValidationWarning
                  text={errors.amountOfKeys?.message}
                  handleInitErrors={() => clearErrors('amountOfKeys')}
                  Component={
                    <Controller
                      control={control}
                      name="amountOfKeys"
                      rules={{
                        min: {
                          value: 1,
                          message:
                            ValidationWarningMessage.AmountOfKeysExceeded,
                        },
                        required: ValidationWarningMessage.Required,
                      }}
                      render={({ field: { value, onChange } }) => (
                        <Input
                          value={value}
                          onChange={({ target: { value } }) =>
                            onChange(transformAmountOfKeysInput(value))
                          }
                          inputProps={{
                            min: 1,
                          }}
                          type="number"
                        />
                      )}
                    />
                  }
                />
              </FieldContainer>

              <FieldContainer>
                <ControlLabel>
                  {t('##attribute#incident#uds_typeofthekey')}
                </ControlLabel>
                <Input {...register('typeOfTheKey')} />
              </FieldContainer>

              <FieldContainer>
                <ControlLabel>
                  {t('##attribute#incident#uds_keynumber')}
                </ControlLabel>
                <Input {...register('keyNumber')} />
              </FieldContainer>
            </Stack>

            <Stack flex={1} spacing={2.5}>
              <FieldContainer>
                <ControlLabel>Lisätiedot</ControlLabel>
                <ValidationWarning
                  text={errors.additionalInformation?.message}
                  handleInitErrors={() => clearErrors('additionalInformation')}
                  Component={
                    <StyledTextarea
                      {...register('additionalInformation', {
                        maxLength: {
                          message:
                            ValidationWarningMessage.AdditionalInformationLengthExceedLimit,
                          value: ADDTIONAL_INFORMATION_MAX_LENGTH,
                        },
                      })}
                    />
                  }
                />
              </FieldContainer>

              <CheckboxContainer>
                <Controller
                  control={control}
                  name="sendSms"
                  render={({ field: { value, onChange } }) => (
                    <Checkbox checked={value} onChange={onChange} />
                  )}
                />
                <CheckboxLabel>
                  {t('##attribute#incident#uds_needsendsms')}
                </CheckboxLabel>
              </CheckboxContainer>

              <CheckboxContainer>
                <Controller
                  control={control}
                  name="sendEmail"
                  render={({ field: { value, onChange } }) => (
                    <Checkbox checked={value} onChange={onChange} />
                  )}
                />
                <CheckboxLabel>
                  {t('##attribute#incident#uds_needsendemail')}
                </CheckboxLabel>
              </CheckboxContainer>

              <FieldContainer>
                <ControlLabel>{t('G_OtherInformation')}</ControlLabel>
                <Controller
                  control={control}
                  name="files"
                  render={({ field: { value, onChange } }) => (
                    <Dropzone value={value} onChange={onChange} />
                  )}
                />
              </FieldContainer>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button color="neutral" onClick={handleCloseDialog}>
              {t('#portal.common.button.cancel')}
            </Button>
            <LoadingButton
              color="success"
              disabled={isCreatingCase}
              loading={isCreatingCase}
              type="submit"
            >
              {t('Send')}
            </LoadingButton>
          </Stack>
        </Stack>
      </PageLayout>
    </Dialog>
  );
};

export default KeyOrderCreateForm;
